import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
} from "antd";
import {
  useAccountingServiceGetPaymentAccounts,
  useAccountingServiceGetExpenseCategories,
  useAccountingServiceUpdateExpense,
  useAccountingServiceGetExpensesKey,
} from "api/queries";
import dayjs from "dayjs";
import qs from "qs";
import { useState, useEffect } from "react";

interface IEditExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  queryClient: any;
  defaultValues: any;
}

const EditExpenseModal: React.FC<IEditExpenseModalProps> = ({
  isOpen,
  onClose,
  queryClient,
  defaultValues,
}) => {
  const [filters, setFilters] = useState("");
  const [categoryFilters, setCategoryFilters] = useState("");

  const [form] = Form.useForm();

  const { data, isLoading, isRefetching, refetch } =
    useAccountingServiceGetPaymentAccounts({
      filters,
      pagination: qs.stringify({ limit: 100, offset: 0 }),
      sort: "",
    });

  const {
    data: categoryData,
    isLoading: isCategoryLoading,
    isRefetching: isCategoryRefetching,
    refetch: refetchCategory,
  } = useAccountingServiceGetExpenseCategories({
    filters: categoryFilters,
    pagination: qs.stringify({ limit: 100, offset: 0 }),
    sort: "",
  });

  const { mutate, isPending } = useAccountingServiceUpdateExpense({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useAccountingServiceGetExpensesKey],
      });
      form.resetFields();
      onClose();
    },
    onError: (err: any) => {
      message.error(err?.message || err || "Failed to update expense");
    },
  });

  const handleSubmit = (values: any) => {
    mutate({
      requestBody: values,
      idExpense: defaultValues.idExpense,
    });
  };

  const handleSearch = (value: string) => {
    setFilters(
      qs.stringify({
        accountName: {
          $ilike: `${value}%`,
        },
      })
    );
  };

  const handleCategorySearch = (value: string) => {
    setCategoryFilters(
      qs.stringify({
        name: {
          $ilike: `${value}%`,
        },
      })
    );
  };

  useEffect(() => {
    refetch();
  }, [filters]);

  useEffect(() => {
    refetchCategory();
  }, [categoryFilters]);

  useEffect(() => {
    form.setFieldsValue({
      ...defaultValues,
      expenseDate: dayjs(defaultValues?.expenseDate) || dayjs(),
      paymentAccount: defaultValues?.paymentAccount?.idAccount || 0,
      expenseCategory: defaultValues?.expenseCategory?.idExpenseCategory || 0,
    });
  }, [form, defaultValues, isOpen]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title="Edit Expense"
      onOk={() => form.submit()}
      okButtonProps={{ loading: isPending }}
      cancelButtonProps={{ disabled: isPending }}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name="expenseName"
              label="Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <Form.Item
              name="expenseAmount"
              label="Amount"
              rules={[{ required: true }]}
            >
              <InputNumber min={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <Form.Item
              name="expenseDate"
              label="Date"
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <Form.Item
              name="paymentAccount"
              label="Payment Account"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                loading={isLoading || isRefetching}
                options={data?.items?.map((item) => ({
                  label: item.accountName,
                  value: item.idAccount,
                }))}
                filterOption={false}
                notFoundContent={false}
                onSearch={handleSearch}
                allowClear
                onClear={() => setFilters("")}
              />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <Form.Item
              name="expenseCategory"
              label="Category"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                loading={isCategoryLoading || isCategoryRefetching}
                options={categoryData?.items?.map((item) => ({
                  label: item.name,
                  value: item.idExpenseCategory,
                }))}
                filterOption={false}
                notFoundContent={false}
                onSearch={handleCategorySearch}
                allowClear
                onClear={() => setCategoryFilters("")}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="expenseDescription"
              label="Description"
              rules={[{ required: true }]}
            >
              <Input.TextArea />{" "}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default EditExpenseModal;
